/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-underscore-dangle, no-alert, react-hooks/exhaustive-deps, no-plusplus, no-await-in-loop, no-restricted-syntax */
import { useState, useEffect, useCallback, useRef } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import { Button, LoadingSpinner, Toast } from './common';
import AssessmentList from './assessment/AssessmentList';
import AssessmentConfig from './assessment/AssessmentConfig';
import QuestionList from './questions/QuestionList';
import ReferenceInput from './reference/ReferenceInput';
import { statusStyles, getQuestionTypeLabel, formatDate } from './utils/formatting';

interface Assessment {
  _id: string
  title: string
  description?: string
  configuration?: {
    questionCount: number
    questionType: string
    answerOptionsCount: number
  }
  questions: Question[]
  referenceDocuments?: ReferenceDocument[]
  status: string
  createdAt: string
  publishedAt?: string
}

interface Question {
  _id: string
  questionText: string
  type: 'multiple_choice' | 'true_false'
  options: { id: string; text: string; isCorrect: boolean }[]
  correctAnswer?: boolean
  explanation?: string
}

interface ReferenceDocument {
  id: string
  fileName: string
  ragStatus: string | null
  ragError?: string
}

interface ToastState {
  id: number
  message: string
  type: 'success' | 'error' | 'info'
}

interface Config {
  title: string
  description: string
  questionCount: number
  questionType: string
  answerOptionsCount: string
  generationPrompt: string
}

type View = 'list' | 'create' | 'edit';

const steps = [
  { number: 1, label: 'Configure' },
  { number: 2, label: 'Reference (Optional)' },
  { number: 3, label: 'Generate & Edit' },
];

// Helper functions to avoid nested ternaries
const getDocContainerClass = (ragStatus: string | null): string => {
  if (ragStatus === 'ready' || ragStatus === null) {
    return 'bg-green-900/20 border border-green-700/50';
  }
  if (ragStatus === 'failed') {
    return 'bg-red-900/20 border border-red-700/50';
  }
  return 'bg-yellow-900/20 border border-yellow-700/50';
};

const getDocTextClass = (ragStatus: string | null): string => {
  if (ragStatus === 'ready' || ragStatus === null) return 'text-green-300';
  if (ragStatus === 'failed') return 'text-red-300';
  return 'text-yellow-300';
};

const getDocStatusClass = (ragStatus: string | null): string => {
  if (ragStatus === 'ready') return 'text-green-400';
  if (ragStatus === 'failed') return 'text-red-400';
  if (ragStatus === 'pending' || ragStatus === 'processing') return 'text-yellow-400';
  return 'text-muted-foreground';
};

const getDocStatusLabel = (ragStatus: string | null): string => {
  if (ragStatus === 'ready') return 'RAG ready';
  if (ragStatus === 'failed') return 'RAG failed';
  if (ragStatus === 'pending' || ragStatus === 'processing') return 'Processing...';
  return 'Ready';
};

// Status icon component to avoid nested ternaries
const DocStatusIcon = ({ ragStatus }: { ragStatus: string | null }) => {
  if (ragStatus === 'pending' || ragStatus === 'processing') {
    return <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-400 border-t-transparent mr-2 flex-shrink-0" />;
  }
  if (ragStatus === 'failed') {
    return (
      <svg className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
};

const BASE_URL = process.env.REACT_APP_API_URI || 'http://localhost:5001';

// SSE helper for streaming endpoints
const streamSSE = async (
  url: string,
  body: object,
  token: string,
  onChunk?: (content: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const response = await fetch(`${BASE_URL}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let result: any = null;
  let done = false;

  const processLine = (line: string) => {
    if (line.startsWith('data: ')) {
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'chunk' && data.content && onChunk) {
          onChunk(data.content);
        } else if (data.type === 'complete') {
          return { complete: true, data: data.data };
        } else if (data.type === 'error') {
          throw new Error(data.error || 'Stream error');
        }
      } catch (e) {
        // Skip non-JSON lines
      }
    }
    return null;
  };

  // eslint-disable-next-line no-await-in-loop, no-restricted-syntax
  while (!done) {
    // eslint-disable-next-line no-await-in-loop
    const chunk = await reader.read();
    done = chunk.done;
    if (chunk.value) {
      buffer += decoder.decode(chunk.value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < lines.length; i++) {
        const lineResult = processLine(lines[i]);
        if (lineResult?.complete) {
          result = lineResult.data;
        }
      }
    }
  }

  return result;
};

const AssessmentCreatorApp = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const [view, setView] = useState<View>('list');
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [currentAssessment, setCurrentAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const toastIdRef = useRef(0);

  // Reference documents tracking
  const [referenceDocIds, setReferenceDocIds] = useState<string[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<ReferenceDocument[]>([]);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [config, setConfig] = useState<Config>({
    title: '',
    description: '',
    questionCount: 10,
    questionType: 'multiple_choice',
    answerOptionsCount: '4',
    generationPrompt: '',
  });

  // Toast helpers
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    toastIdRef.current += 1;
    const id = toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const showSuccess = (message: string) => showToast(message, 'success');
  const showError = (message: string) => showToast(message, 'error');

  // Fetch assessments
  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get('/assessment-creator/assessments');
      setAssessments(response.data.data || []);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // Poll for RAG status
  useEffect(() => {
    const docsNeedingPoll = uploadedDocs.filter(
      doc => doc.ragStatus && doc.ragStatus !== 'ready' && doc.ragStatus !== 'failed'
    );

    if (docsNeedingPoll.length > 0) {
      pollIntervalRef.current = setInterval(async () => {
        const updates = await Promise.all(
          docsNeedingPoll.map(async (doc) => {
            try {
              const { data } = await axiosPrivate.get(`/assessment-creator/reference/${doc.id}/status`);
              return { id: doc.id, ragStatus: data.ragStatus, ragError: data.ragError };
            } catch (error) {
              console.error('Failed to poll RAG status:', error);
              return null;
            }
          })
        );

        setUploadedDocs(prev => prev.map(doc => {
          const update = updates.find(u => u?.id === doc.id);
          if (update) {
            return { ...doc, ragStatus: update.ragStatus, ragError: update.ragError };
          }
          return doc;
        }));

        const allDone = updates.every(u => !u || u.ragStatus === 'ready' || u.ragStatus === 'failed');
        if (allDone && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      }, 2000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [uploadedDocs, axiosPrivate]);

  const resetForm = () => {
    setConfig({
      title: '',
      description: '',
      questionCount: 10,
      questionType: 'multiple_choice',
      answerOptionsCount: '4',
      generationPrompt: '',
    });
    setCurrentAssessment(null);
    setCurrentStep(1);
    setReferenceDocIds([]);
    setUploadedDocs([]);
  };

  const handleCreateNew = () => {
    resetForm();
    setView('create');
  };

  const handleEditAssessment = async (id: string) => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/assessment-creator/assessments/${id}`);
      const assessment = response.data.data;
      setCurrentAssessment(assessment);
      setConfig({
        title: assessment.title,
        description: assessment.description || '',
        questionCount: assessment.configuration?.questionCount || 10,
        questionType: assessment.configuration?.questionType || 'multiple_choice',
        answerOptionsCount: String(assessment.configuration?.answerOptionsCount || 4),
        generationPrompt: '',
      });
      setView('edit');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssessment = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    try {
      await axiosPrivate.delete(`/assessment-creator/assessments/${id}`);
      fetchAssessments();
      showSuccess('Assessment deleted');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete assessment');
    }
  };

  const handlePublishAssessment = async (id: string) => {
    try {
      await axiosPrivate.patch(`/assessment-creator/assessments/${id}/publish`);
      fetchAssessments();
      // eslint-disable-next-line no-underscore-dangle
      if (currentAssessment?._id === id) {
        setCurrentAssessment(prev => prev ? { ...prev, status: 'published' } : prev);
      }
      showSuccess('Assessment published successfully!');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to publish assessment');
    }
  };

  const handleReferenceSubmit = async (reference: { type: 'files' | 'text'; data: File[] | string }) => {
    setLoading(true);
    try {
      if (reference.type === 'files') {
        const formData = new FormData()
        ;(reference.data as File[]).forEach((file) => formData.append('files', file));

        const response = await axiosPrivate.post('/assessment-creator/upload/files', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        /* eslint-disable no-underscore-dangle */
        const newDocs = response.data.data.documents.map((doc: any) => ({
          id: doc._id || doc.id,
          fileName: doc.file?.originalName || doc.fileName,
          ragStatus: doc.ragStatus
        }));
        /* eslint-enable no-underscore-dangle */
        setReferenceDocIds(prev => [...prev, ...newDocs.map((d: ReferenceDocument) => d.id)]);
        setUploadedDocs(prev => [...prev, ...newDocs]);
        showSuccess(`${newDocs.length} file(s) added successfully!`);
      } else {
        const response = await axiosPrivate.post('/assessment-creator/upload/text', { text: reference.data });
        const doc = response.data.data;
        // eslint-disable-next-line no-underscore-dangle
        const docId = doc._id || doc.id;
        setReferenceDocIds(prev => [...prev, docId]);
        setUploadedDocs(prev => [...prev, {
          id: docId,
          fileName: 'Pasted text',
          ragStatus: doc.ragStatus
        }]);
        showSuccess('Reference material added successfully!');
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to upload');
    } finally {
      setLoading(false);
    }
  };

  const createAndGenerate = async () => {
    setLoading(true);
    try {
      // Create assessment
      const createResponse = await axiosPrivate.post('/assessment-creator/assessments', {
        title: config.title,
        description: config.description,
        configuration: {
          questionCount: parseInt(String(config.questionCount), 10),
          questionType: config.questionType,
          answerOptionsCount: parseInt(config.answerOptionsCount, 10),
        },
        referenceDocumentIds: referenceDocIds,
      });

      const assessment = createResponse.data.data;
      // eslint-disable-next-line no-underscore-dangle
      const assessmentId = assessment._id;
      setCurrentAssessment(assessment);

      // Generate questions using streaming endpoint
      await streamSSE(
        '/assessment-creator/questions/generate/stream',
        {
          assessmentId,
          prompt: config.generationPrompt,
        },
        auth.accessToken
      );

      // Fetch updated assessment with questions
      const updatedResponse = await axiosPrivate.get(`/assessment-creator/assessments/${assessmentId}`);
      setCurrentAssessment(updatedResponse.data.data);

      showSuccess('Questions generated successfully!');
      setCurrentStep(3);
    } catch (err: any) {
      showError(err.message || 'Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  const regenerateAllQuestions = async () => {
    if (!currentAssessment) return;
    setLoading(true);
    try {
      // eslint-disable-next-line no-underscore-dangle
      const assessmentId = currentAssessment._id;

      // Generate questions using streaming endpoint
      await streamSSE(
        '/assessment-creator/questions/generate/stream',
        {
          assessmentId,
          prompt: config.generationPrompt,
        },
        auth.accessToken
      );

      const response = await axiosPrivate.get(`/assessment-creator/assessments/${assessmentId}`);
      setCurrentAssessment(response.data.data);
      showSuccess('All questions regenerated!');
    } catch (err: any) {
      showError(err.message || 'Failed to regenerate questions');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateQuestion = async (questionId: string, prompt?: string) => {
    try {
      await streamSSE(
        `/assessment-creator/questions/${questionId}/regenerate/stream`,
        { prompt },
        auth.accessToken
      );

      // Refresh assessment
      if (currentAssessment) {
        // eslint-disable-next-line no-underscore-dangle
        const id = currentAssessment._id;
        const response = await axiosPrivate.get(`/assessment-creator/assessments/${id}`);
        setCurrentAssessment(response.data.data);
      }
      showSuccess('Question regenerated!');
    } catch (err: any) {
      showError(err.message || 'Failed to regenerate question');
    }
  };

  const handleRegenerateAnswer = async (questionId: string, answerIndex: number, prompt?: string) => {
    try {
      await streamSSE(
        `/assessment-creator/questions/${questionId}/regenerate-answer/stream`,
        { answerIndex, prompt },
        auth.accessToken
      );

      if (currentAssessment) {
        // eslint-disable-next-line no-underscore-dangle
        const id = currentAssessment._id;
        const response = await axiosPrivate.get(`/assessment-creator/assessments/${id}`);
        setCurrentAssessment(response.data.data);
      }
      showSuccess('Answer regenerated!');
    } catch (err: any) {
      showError(err.message || 'Failed to regenerate answer');
    }
  };

  const handleUpdateQuestion = async (question: Question) => {
    try {
      // eslint-disable-next-line no-underscore-dangle
      await axiosPrivate.put(`/assessment-creator/questions/${question._id}`, {
        questionText: question.questionText,
        options: question.options,
        correctAnswer: question.correctAnswer,
        explanation: question.explanation,
      });

      if (currentAssessment) {
        setCurrentAssessment(prev => prev ? {
          ...prev,
          // eslint-disable-next-line no-underscore-dangle
          questions: prev.questions.map(q => q._id === question._id ? question : q)
        } : prev);
      }
      showSuccess('Question updated!');
    } catch (err: any) {
      showError('Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      await axiosPrivate.delete(`/assessment-creator/questions/${questionId}`);

      if (currentAssessment) {
        setCurrentAssessment(prev => prev ? {
          ...prev,
          // eslint-disable-next-line no-underscore-dangle
          questions: prev.questions.filter(q => q._id !== questionId)
        } : prev);
      }
      showSuccess('Question deleted');
    } catch (err: any) {
      showError('Failed to delete question');
    }
  };

  const canProceedToStep2 = config.title.trim().length > 0 && config.questionCount > 0;
  const hasReferenceDocuments = referenceDocIds.length > 0;
  const isRagProcessing = uploadedDocs.some(
    doc => doc.ragStatus === 'pending' || doc.ragStatus === 'processing'
  );
  const isPublished = currentAssessment?.status === 'published';

  // List View
  if (view === 'list') {
    return (
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Assessments</h1>
            <p className="text-muted-foreground mt-1">Manage your AI-generated assessments</p>
          </div>
          <Button onClick={handleCreateNew}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Assessment
          </Button>
        </div>

        <AssessmentList
          assessments={assessments}
          loading={loading}
          onEdit={handleEditAssessment}
          onDelete={handleDeleteAssessment}
          onPublish={handlePublishAssessment}
        />

        {/* Toast Container */}
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Create View
  if (view === 'create') {
    return (
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => { resetForm(); setView('list'); }}
            className="text-muted-foreground hover:text-foreground text-sm font-medium flex items-center mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Assessments
          </button>
          <h1 className="text-2xl font-semibold text-foreground">Create Assessment</h1>
          <p className="text-muted-foreground mt-1">
            Generate questions from your reference material using AI
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex items-center">
                  <div
                    className={`w-9 h-9 rounded-md flex items-center justify-center font-medium text-sm transition-colors ${
                      currentStep >= step.number
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${
                      currentStep >= step.number ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-16 h-0.5 mx-4 rounded ${
                      currentStep > step.number ? 'bg-primary' : 'bg-border'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-3xl mx-auto">
          {/* Step 1: Configuration */}
          {currentStep === 1 && (
            <div>
              <AssessmentConfig config={config} onChange={setConfig} />
              <div className="mt-6 flex justify-end">
                <Button disabled={!canProceedToStep2} onClick={() => setCurrentStep(2)}>
                  Continue
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Reference Material */}
          {currentStep === 2 && (
            <div>
              <ReferenceInput
                loading={loading}
                onSubmit={handleReferenceSubmit}
                onError={showError}
              />

              {/* Uploaded Documents List */}
              {uploadedDocs.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {uploadedDocs.length} reference{uploadedDocs.length !== 1 ? 's' : ''} uploaded
                  </p>
                  {uploadedDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`p-3 rounded-lg flex items-center justify-between ${getDocContainerClass(doc.ragStatus)}`}
                    >
                      <div className="flex items-center min-w-0">
                        <DocStatusIcon ragStatus={doc.ragStatus} />
                        <span className={`text-sm truncate ${getDocTextClass(doc.ragStatus)}`}>
                          {doc.fileName}
                        </span>
                      </div>
                      <span className={`text-xs ml-2 flex-shrink-0 ${getDocStatusClass(doc.ragStatus)}`}>
                        {getDocStatusLabel(doc.ragStatus)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!hasReferenceDocuments && (
                <div className="mt-4 p-4 rounded-lg bg-amber-900/20 border border-amber-700/50">
                  <p className="text-sm text-amber-300">
                    No reference material added. Questions will be generated based on your configuration and custom prompt only.
                  </p>
                </div>
              )}

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button
                  loading={loading}
                  disabled={isRagProcessing}
                  onClick={createAndGenerate}
                >
                  {isRagProcessing ? 'Waiting for RAG...' : 'Generate Questions'}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Generated Questions */}
          {currentStep === 3 && (
            <div>
              <div className="rounded-lg border border-border bg-card p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground">{config.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {currentAssessment?.questions?.length || 0} questions generated
                    </p>
                  </div>
                  <Button variant="secondary" loading={loading} onClick={regenerateAllQuestions}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate All
                  </Button>
                </div>
              </div>

              <QuestionList
                questions={currentAssessment?.questions || []}
                loading={loading}
                isPublished={false}
                onRegenerate={handleRegenerateQuestion}
                onRegenerateAnswer={handleRegenerateAnswer}
                onUpdate={handleUpdateQuestion}
                onDelete={handleDeleteQuestion}
              />

              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => { showSuccess('Assessment saved as draft'); setView('list'); }}>
                  Save as Draft
                </Button>
                <Button
                  variant="success"
                  loading={loading}
                  // eslint-disable-next-line no-underscore-dangle
                  onClick={() => currentAssessment && handlePublishAssessment(currentAssessment._id).then(() => setView('list'))}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Publish Assessment
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Toast Container */}
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Edit View
  if (view === 'edit' && currentAssessment) {
    return (
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => { resetForm(); setView('list'); }}
            className="text-muted-foreground hover:text-foreground text-sm font-medium flex items-center mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Assessments
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-semibold text-foreground">{currentAssessment.title}</h1>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-md border capitalize ${statusStyles[currentAssessment.status]}`}
                >
                  {currentAssessment.status}
                </span>
              </div>
              {currentAssessment.description && (
                <p className="text-muted-foreground mt-1">{currentAssessment.description}</p>
              )}
            </div>

            {!isPublished && (
              <div className="flex space-x-2">
                <Button variant="secondary" loading={loading} onClick={regenerateAllQuestions}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Regenerate All
                </Button>
                {/* eslint-disable-next-line no-underscore-dangle */}
                <Button variant="success" loading={loading} onClick={() => handlePublishAssessment(currentAssessment._id)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Publish
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Assessment Info */}
        <div className="rounded-lg border border-border bg-card p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground block">Questions</span>
              <span className="font-medium text-foreground">
                {currentAssessment.questions?.length || 0}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block">Type</span>
              <span className="font-medium text-foreground">
                {getQuestionTypeLabel(currentAssessment.configuration?.questionType)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground block">Created</span>
              <span className="font-medium text-foreground">
                {formatDate(currentAssessment.createdAt, { includeTime: true })}
              </span>
            </div>
            {currentAssessment.publishedAt && (
              <div>
                <span className="text-muted-foreground block">Published</span>
                <span className="font-medium text-foreground">
                  {formatDate(currentAssessment.publishedAt, { includeTime: true })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Published Notice */}
        {isPublished && (
          <div className="bg-secondary/50 border border-border rounded-lg p-4 mb-6 flex items-center">
            <svg className="w-5 h-5 text-muted-foreground mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-muted-foreground">
              This assessment is published. Changes are limited to protect data integrity.
            </p>
          </div>
        )}

        {/* Questions */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground">Questions</h2>
        </div>

        <QuestionList
          questions={currentAssessment.questions || []}
          loading={loading}
          isPublished={isPublished}
          onRegenerate={handleRegenerateQuestion}
          onRegenerateAnswer={handleRegenerateAnswer}
          onUpdate={handleUpdateQuestion}
          onDelete={handleDeleteQuestion}
        />

        {/* Toast Container */}
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default AssessmentCreatorApp;
